import { useLocale, type Locale } from "./locale";

export type DeepPartial<T> = T extends readonly (infer U)[]
  ? readonly DeepPartial<U>[]
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Deep-merges a translation over the English base. Any key the translation
 * omits keeps its English value, so a partial dictionary never blanks the page.
 */
export function mergeDict<T>(base: T, override: unknown): T {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base)) {
    if (!Array.isArray(override)) return base;
    return base.map((item, i) => mergeDict(item, override[i])) as unknown as T;
  }
  if (isPlainObject(base)) {
    if (!isPlainObject(override)) return base;
    const out: Record<string, unknown> = { ...base };
    for (const key of Object.keys(base)) {
      out[key] = mergeDict((base as Record<string, unknown>)[key], override[key]);
    }
    return out as T;
  }
  return (override as T) ?? base;
}

export type Translations<T> = Partial<Record<Locale, DeepPartial<T>>>;

/** Returns the page content for the active locale, falling back to English. */
export function useLocalized<T>(base: T, translations: Translations<T>): T {
  const locale = useLocale();
  return mergeDict(base, translations[locale]);
}
